import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { LogService } from '@shared/services/log.service';
import { retry, tap, timer } from 'rxjs';
import {
  ALLOW_NON_IDEMPOTENT_RETRY,
  createRequestId,
  ENABLE_HTTP_RETRY,
  HTTP_RETRY_OPTIONS,
  type HttpRetryOptions,
  REQUEST_ID,
  REQUEST_ID_HEADER,
  SKIP_GLOBAL_HTTP_ERROR
} from './http-context';

const DEFAULT_RETRY_OPTIONS: HttpRetryOptions = {
  count: 3,
  baseDelayMs: 300,
  maxDelayMs: 2000
};

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function normalizeRetryOptions(options: HttpRetryOptions | null): HttpRetryOptions {
  const source = options ?? DEFAULT_RETRY_OPTIONS;
  const count = Math.max(0, Math.floor(source.count));
  const baseDelayMs = Math.max(0, Math.floor(source.baseDelayMs));
  const maxDelayMs = Math.max(baseDelayMs, Math.floor(source.maxDelayMs));

  return { count, baseDelayMs, maxDelayMs };
}

export const globalHttpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const logger = inject(LogService);
  const requestId = req.context.get(REQUEST_ID) ?? createRequestId('http');
  const request = req.clone({
    context: req.context.set(REQUEST_ID, requestId),
    setHeaders: {
      [REQUEST_ID_HEADER]: requestId
    }
  });

  const skipError = request.context.get(SKIP_GLOBAL_HTTP_ERROR);
  const enableRetry = request.context.get(ENABLE_HTTP_RETRY);
  const allowNonIdempotentRetry = request.context.get(ALLOW_NON_IDEMPOTENT_RETRY);
  const method = request.method.toUpperCase();
  const isIdempotent = IDEMPOTENT_METHODS.has(method);
  const shouldRetry = enableRetry && (isIdempotent || allowNonIdempotentRetry);
  const retryOptions = normalizeRetryOptions(request.context.get(HTTP_RETRY_OPTIONS));

  const stream = shouldRetry
    ? next(request).pipe(
      retry({
        count: retryOptions.count,
        delay: (_error, retryCount) => {
          const delayMs = Math.min(retryOptions.baseDelayMs * (2 ** (retryCount - 1)), retryOptions.maxDelayMs);
          return timer(delayMs);
        }
      })
    )
    : next(request);

  if (skipError) {
    return stream;
  }

  return stream.pipe(
    tap({
      error: (error: HttpErrorResponse) => {
        logger.error('HTTP request failed', {
          requestId,
          method: request.method,
          url: request.url,
          status: error.status,
          errorName: error.name
        });
      }
    })
  );
};
